import React, { useState, useEffect, useCallback } from 'react';
import { Search, Eye, Calendar, Phone, X, RefreshCw, CheckCircle } from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import { getAllBookings, updateBookingAdmin, confirmBookingByAdmin, rejectBookingByAdmin, getMechanics } from '../api';
import './AppointmentsPage.css';

const STATUS_META = {
  PENDING_CONFIRMATION: { label:'Pending Confirmation', cls:'badge-neutral'  },
  pending_confirmation: { label:'Pending Confirmation', cls:'badge-neutral'  },
  CONFIRMED:            { label:'Confirmed',            cls:'badge-info'     },
  confirmed:            { label:'Confirmed',            cls:'badge-info'     },
  IN_PROGRESS:          { label:'In Progress',          cls:'badge-warning'  },
  'in-progress':        { label:'In Progress',          cls:'badge-warning'  },
  WAITING:              { label:'Waiting',              cls:'badge-info'     },
  waiting:              { label:'Waiting',              cls:'badge-info'     },
  COMPLETED:            { label:'Completed',            cls:'badge-success'  },
  completed:            { label:'Completed',            cls:'badge-success'  },
  CANCELLED:            { label:'Cancelled',            cls:'badge-neutral'  },
  cancelled:            { label:'Cancelled',            cls:'badge-neutral'  },
  REJECTED:             { label:'Rejected',             cls:'badge-danger'   },
  rejected:             { label:'Rejected',             cls:'badge-danger'   },
};

// Status values the backend BookingStatus enum accepts
const STATUS_OPTIONS = [
  { value:'PENDING_CONFIRMATION', label:'Pending Confirmation' },
  { value:'CONFIRMED',            label:'Confirmed'            },
  { value:'IN_PROGRESS',          label:'In Progress'          },
  { value:'WAITING',              label:'Waiting'              },
  { value:'COMPLETED',            label:'Completed'            },
  { value:'CANCELLED',            label:'Cancelled'            },
];

const PICKUP_LABEL = {
  VEHICLE_PICKUP:   'Vehicle Pickup',
  CUSTOMER_DROPOFF: 'Customer Drop-off',
  SELF_DRIVE_IN:    'Self Drive-in',
};

// Sort newest first — pending confirmations float to top, then by createdAt / date desc
const sortBookings = (arr) => (arr || []).slice().sort((a, b) => {
  const aPending = (a.status || '').toUpperCase() === 'PENDING_CONFIRMATION';
  const bPending = (b.status || '').toUpperCase() === 'PENDING_CONFIRMATION';
  if (aPending !== bPending) return aPending ? -1 : 1;
  const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
  const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
  if (tb !== ta) return tb - ta;
  const da = a.date ? new Date(a.date).getTime() : 0;
  const db = b.date ? new Date(b.date).getTime() : 0;
  if (db !== da) return db - da;
  return (b.id || '').localeCompare(a.id || '');
});

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [mechanics,    setMechanics]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewItem,     setViewItem]     = useState(null);
  const [saving,       setSaving]       = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bookings, mechs] = await Promise.all([
        getAllBookings(),
        getMechanics().catch(() => []),
      ]);
      setAppointments(sortBookings(bookings));
      setMechanics(mechs || []);
    } catch (e) {
      console.error('Load failed:', e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 15s for fresh data
  useEffect(() => {
    const iv = setInterval(async () => {
      if (document.hidden) return; // don't poll when tab not visible
      try { const data = await getAllBookings(); setAppointments(sortBookings(data||[])); } catch {}
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  /* ── Status / mechanic update ── */
  const update = async (id, body) => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      const updated = await updateBookingAdmin(id, body);
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...updated } : a));
      if (viewItem?.id === id) setViewItem(v => ({ ...v, ...updated }));
    } catch (e) {
      alert('Update failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  };

  /* ── Quick confirm ── */
  const quickConfirm = async id => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      const updated = await confirmBookingByAdmin(id);
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...updated, status:'CONFIRMED' } : a));
      if (viewItem?.id === id) setViewItem(v => ({ ...v, ...updated, status:'CONFIRMED' }));
    } catch (e) {
      alert('Confirm failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  };

  /* ── Quick reject ── */
  const quickReject = async id => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      const updated = await rejectBookingByAdmin(id, 'Rejected by admin');
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...updated, status:'REJECTED' } : a));
      if (viewItem?.id === id) setViewItem(v => ({ ...v, ...updated, status:'REJECTED' }));
    } catch (e) {
      alert('Reject failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  };

  /* ── Field normalisation (API uses camelCase) ── */
  const getName    = a => a.ownerName    || a.customer  || '—';
  const getVehicle = a => a.vehicleMake  ? `${a.vehicleMake} ${a.vehicleModel||''}`.trim() : (a.vehicle||'—');
  const getReg     = a => a.regNumber    || a.reg       || '—';
  const getService = a => Array.isArray(a.services) ? a.services.join(', ') : (a.service||'—');
  const getPhone   = a => a.phone        || '—';
  const getDate    = a => a.date         || '—';
  const getTime    = a => a.timeSlot     || a.time      || '—';
  const getMech    = a => a.mechanicName || a.mechanic  || 'Unassigned';
  const getPickup  = a => PICKUP_LABEL[a.pickupType] || a.pickupType || '—';
  const getAmount  = a => a.estimatedAmount ? `₹${Number(a.estimatedAmount).toLocaleString('en-IN')}` : (a.amount||'—');
  const getStatus  = a => a.status?.toUpperCase?.() || a.status || 'PENDING_CONFIRMATION';

  const mechOptions = [
    'Unassigned',
    ...mechanics.map(m => m.name),
  ];

  const filtered = appointments.filter(a => {
    const q = search.trim().toLowerCase();
    const matchSearch = !q
      || (a.id||'').toLowerCase().includes(q)
      || getName(a).toLowerCase().includes(q)
      || getVehicle(a).toLowerCase().includes(q)
      || getReg(a).toLowerCase().includes(q)
      || getService(a).toLowerCase().includes(q)
      || getPhone(a).toLowerCase().includes(q)
      || getMech(a).toLowerCase().includes(q)
      || getDate(a).toLowerCase().includes(q)
      || (a.email||'').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all'
      || (a.status || '').toUpperCase() === statusFilter.toUpperCase()
      || (a.status || '').toLowerCase().replace('_','-') === statusFilter.toLowerCase().replace('_','-');
    return matchSearch && matchStatus;
  });

  return (
    <>
      <TopBar title="Appointments" subtitle="All customer bookings — live from database"/>
      <div className="admin-content">
        <div className="page-hdr">
          <div className="page-hdr__left">
            <h1>All Appointments</h1>
            <p>{filtered.length} of {appointments.length} bookings</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading?'spinning':''}/>{loading?'Loading…':'Refresh'}
          </button>
        </div>

        {/* Toolbar */}
        <div className="appt-toolbar">
          <div className="appt-search">
            <Search size={14}/>
            <input className="input" placeholder="Search by ID, customer, vehicle, service…"
              value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
          <select className="input" style={{ width:'auto', minWidth:200 }}
            value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:'var(--text-muted)' }}>
            <div className="appt-spinner"/>
            <p style={{ marginTop:12, fontSize:13 }}>Loading from database…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="appt-empty">
            <Calendar size={40}/>
            <p>{appointments.length === 0
              ? 'No bookings yet — they appear here when customers book.'
              : 'No bookings match your filter.'}</p>
          </div>
        ) : (
          <div className="appt-table-wrap">
            <table className="appt-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Vehicle</th>
                  <th>Service</th>
                  <th>Date & Time</th>
                  <th>Mechanic</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th style={{ width:100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const status = getStatus(a);
                  const meta   = STATUS_META[status] || STATUS_META[a.status] || { label: a.status, cls:'badge-neutral' };
                  const isPending = status === 'PENDING_CONFIRMATION';
                  const isNew     = isPending;
                  const isSaving  = saving[a.id];

                  return (
                    <tr key={a.id} className={`appt-row ${isNew ? 'appt-row--new' : ''}`}>
                      <td className="appt-id">
                        {isNew && <span className="appt-new-dot" title="New"/>}
                        {a.id}
                      </td>
                      <td>
                        <div className="appt-customer">
                          <span className="appt-customer__name">{getName(a)}</span>
                          {getPhone(a) !== '—' && (
                            <span className="appt-customer__phone"><Phone size={10}/>{getPhone(a)}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="appt-vehicle">
                          <span>{getVehicle(a)}</span>
                          <span className="appt-reg">{getReg(a)}</span>
                        </div>
                      </td>
                      <td className="appt-service">{getService(a)}</td>
                      <td className="appt-date">
                        {getDate(a)}
                        <span className="appt-time">{getTime(a)}</span>
                      </td>
                      <td>
                        <select className="appt-mechanic-select"
                          value={getMech(a)} disabled={isSaving}
                          onChange={e => update(a.id, { mechanicName: e.target.value })}>
                          {mechOptions.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </td>
                      <td>
                        {/* Show confirm/reject buttons for pending, dropdown for others */}
                        {isPending ? (
                          <div style={{ display:'flex', gap:5 }}>
                            <button className="btn btn-sm btn-primary" style={{ padding:'4px 8px', fontSize:11 }}
                              disabled={isSaving} onClick={() => quickConfirm(a.id)}>
                              <CheckCircle size={11}/> Confirm
                            </button>
                            <button className="btn btn-sm btn-danger" style={{ padding:'4px 8px', fontSize:11 }}
                              disabled={isSaving} onClick={() => quickReject(a.id)}>
                              <X size={11}/>
                            </button>
                          </div>
                        ) : (
                          <select className={`appt-status-select badge ${meta.cls}`}
                            value={status} disabled={isSaving}
                            onChange={e => update(a.id, { status: e.target.value })}>
                            {STATUS_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="appt-amount">{getAmount(a)}</td>
                      <td>
                        <button className="tbl-btn" onClick={() => setViewItem(a)} title="View details">
                          <Eye size={13}/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {viewItem && (
        <div className="modal-overlay" onClick={() => setViewItem(null)}>
          <div className="modal appt-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <div>
                <h3 className="modal__title">{viewItem.id}</h3>
                <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                  {getName(viewItem)} · {getDate(viewItem)} at {getTime(viewItem)}
                </p>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <span className={`badge ${STATUS_META[getStatus(viewItem)]?.cls||'badge-neutral'}`}>
                  {STATUS_META[getStatus(viewItem)]?.label || viewItem.status}
                </span>
                <button className="modal__close" onClick={() => setViewItem(null)}><X size={18}/></button>
              </div>
            </div>

            <div className="appt-detail-grid">
              <div className="appt-detail-block">
                <p className="appt-detail-label">Customer</p>
                <p className="appt-detail-value">{getName(viewItem)}</p>
                <p className="appt-detail-sub">{getPhone(viewItem)}</p>
                <p className="appt-detail-sub">{viewItem.email||'—'}</p>
              </div>
              <div className="appt-detail-block">
                <p className="appt-detail-label">Vehicle</p>
                <p className="appt-detail-value">{getVehicle(viewItem)}</p>
                <p className="appt-detail-sub">Reg: {getReg(viewItem)}</p>
                {viewItem.fuelType && <p className="appt-detail-sub">Fuel: {viewItem.fuelType}</p>}
              </div>
              <div className="appt-detail-block">
                <p className="appt-detail-label">Service</p>
                <p className="appt-detail-value">{getService(viewItem)}</p>
              </div>
              <div className="appt-detail-block">
                <p className="appt-detail-label">Schedule</p>
                <p className="appt-detail-value">{getDate(viewItem)}</p>
                <p className="appt-detail-sub">{getTime(viewItem)}</p>
              </div>
              <div className="appt-detail-block">
                <p className="appt-detail-label">Pickup</p>
                <p className="appt-detail-value">{getPickup(viewItem)}</p>
                {viewItem.address && <p className="appt-detail-sub">{viewItem.address}</p>}
              </div>
              <div className="appt-detail-block">
                <p className="appt-detail-label">Amount</p>
                <p className="appt-detail-value">{getAmount(viewItem)}</p>
              </div>
            </div>

            {viewItem.additionalNotes && (
              <div className="appt-detail-notes">
                <p className="appt-detail-label">Customer Notes</p>
                <p style={{ fontSize:13, color:'var(--text-secondary)', marginTop:6, lineHeight:1.6 }}>
                  {viewItem.additionalNotes}
                </p>
              </div>
            )}

            {/* Quick confirm/reject for pending */}
            {getStatus(viewItem) === 'PENDING_CONFIRMATION' && (
              <div style={{ display:'flex', gap:10, marginTop:16, padding:'14px 0', borderTop:'1px solid var(--border)' }}>
                <button className="btn btn-primary" style={{ flex:1, justifyContent:'center' }}
                  onClick={() => quickConfirm(viewItem.id)}>
                  <CheckCircle size={14}/> Confirm Booking
                </button>
                <button className="btn btn-danger" style={{ flex:1, justifyContent:'center' }}
                  onClick={() => quickReject(viewItem.id)}>
                  <X size={14}/> Reject Booking
                </button>
              </div>
            )}

            <div className="modal-actions" style={{ flexWrap:'wrap', gap:12 }}>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <label className="label" style={{ margin:0 }}>Mechanic:</label>
                <select className="input" style={{ width:'auto' }}
                  value={getMech(viewItem)}
                  onChange={e => update(viewItem.id, { mechanicName: e.target.value })}>
                  {mechOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <label className="label" style={{ margin:0 }}>Status:</label>
                <select className="input" style={{ width:'auto' }}
                  value={getStatus(viewItem)}
                  onChange={e => update(viewItem.id, { status: e.target.value })}>
                  {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}